import { motion } from 'framer-motion'
import {
  Database,
  Calendar,
  ExternalLink,
  Download,
  FileText,
  Server,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'

interface DataSource {
  name: string
  version: string
  date: string
  description: string
  url: string
  downloadUrl?: string
}

const dataSources: DataSource[] = [
  {
    name: 'MIBiG',
    version: '4.0',
    date: 'November 15th, 2024',
    description: 'Minimum Information about a Biosynthetic Gene cluster - Gene cluster information and sequences',
    url: 'https://mibig.secondarymetabolites.org/',
    downloadUrl: 'https://mibig.secondarymetabolites.org/download',
  },
  {
    name: 'UniProt (SwissProt)',
    version: 'Release 2024_06',
    date: 'December 1, 2024',
    description: 'Manually reviewed protein sequence database for BLAST searches and functional annotations',
    url: 'https://www.uniprot.org/',
    downloadUrl: 'https://www.uniprot.org/help/downloads',
  },
  {
    name: 'NCBI BLAST Databases',
    version: 'pdbaa, swissprot',
    date: 'November 30, 2024',
    description: 'Pre-formatted BLAST databases from NCBI for sequence similarity searches',
    url: 'https://ftp.ncbi.nlm.nih.gov/blast/db/',
  },
  {
    name: 'AlphaFold DB',
    version: 'v4',
    date: 'December 1, 2024',
    description: 'Predicted protein structures from DeepMind AlphaFold2',
    url: 'https://alphafold.ebi.ac.uk/',
    downloadUrl: 'https://ftp.ebi.ac.uk/pub/databases/alphafold/',
  },
  {
    name: 'Ligand Expo',
    version: 'PDBML',
    date: 'December 1, 2024',
    description: 'Chemical component dictionary from RCSB PDB for ligand information',
    url: 'http://ligand-expo.rcsb.org/',
    downloadUrl: 'http://ligand-expo.rcsb.org/ld-download.html',
  },
  {
    name: 'PubChem',
    version: 'Identifier Exchange',
    date: 'December 1, 2024',
    description: 'Chemical compound database from NCBI for compound identification and cross-references',
    url: 'https://pubchem.ncbi.nlm.nih.gov/',
    downloadUrl: 'https://pubchem.ncbi.nlm.nih.gov/idexchange/idexchange.cgi',
  },
  {
    name: 'Rhea',
    version: 'Release 136',
    date: 'December 27, 2024',
    description: 'Biochemical reaction database for enzyme reaction annotations',
    url: 'https://www.rhea-db.org/',
    downloadUrl: 'https://www.rhea-db.org/help/download',
  },
  {
    name: 'NCBI CDD',
    version: 'v3.21',
    date: 'November 2024',
    description: 'Conserved Domain Database for protein domain identification',
    url: 'https://www.ncbi.nlm.nih.gov/Structure/cdd/cdd.shtml',
  },
]

const blastResults = [
  {
    name: 'MIBiG vs PDB',
    description: 'BLAST results of MIBiG sequences against PDB protein structures',
    rows: '3,200,000+',
  },
  {
    name: 'MIBiG vs SwissProt',
    description: 'BLAST results of MIBiG sequences against SwissProt database',
    rows: '9,200,000+',
  },
  {
    name: 'Domain vs PDB',
    description: 'BLAST results of conserved domains against PDB structures',
    rows: '3,300,000+',
  },
  {
    name: 'Domain vs SwissProt',
    description: 'BLAST results of conserved domains against SwissProt',
    rows: '10,200,000+',
  },
]

const bulkDownloads = [
  {
    name: 'MIBiG vs PDB (BLAST)',
    filename: 'blast_pdb24.12.tsv.gz',
    size: '113 MB',
    format: 'MIBiG_accession, Protein_ID, Descriptions, PDB_IDs, Chains, Identity, Cover, E-value, Hit_num, HSP_num',
    url: '/db/download/blast_pdb24.12.tsv.gz',
  },
  {
    name: 'MIBiG vs SwissProt (BLAST)',
    filename: 'blast_swissprot24.12.tsv.gz',
    size: '231 MB',
    format: 'MIBiG_accession, Protein_ID, SwissProt_accession, Recname, Identity, Cover, E-value, Hit_num, HSP_num',
    url: '/db/download/blast_swissprot24.12.tsv.gz',
  },
]

export function DataSourcesPage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2">Data Sources</h1>
        <p className="text-gray-400">
          Information about the databases and resources used in this application
        </p>
      </motion.div>

      {/* Current Version Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-accent-cyan/30 bg-accent-cyan/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-accent-cyan/20">
                <Database className="w-8 h-8 text-accent-cyan" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Gene cluster information is based on MIBiG Version 4.0
                </h2>
                <p className="text-gray-400">
                  Last updated: November 15th, 2024
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Data Sources Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Server className="w-5 h-5 text-accent-purple" />
          External Databases
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dataSources.map((source, index) => (
            <motion.div
              key={source.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <Card className="h-full hover:border-white/20 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg">{source.name}</span>
                    <Badge variant="default">{source.version}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-400 mb-3">{source.description}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                    <Calendar className="w-3 h-3" />
                    <span>{source.date}</span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-accent-cyan hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Visit
                    </a>
                    {source.downloadUrl && (
                      <a
                        href={source.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-accent-amber hover:underline"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Pre-computed BLAST Results */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-accent-amber" />
          Pre-computed BLAST Results
        </h2>
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-400 mb-4">
              All BLAST results in this database are pre-computed and stored for fast retrieval.
              The following datasets are available:
            </p>
            <div className="space-y-3">
              {blastResults.map((result) => (
                <div
                  key={result.name}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium text-white">{result.name}</h3>
                    <p className="text-sm text-gray-400">{result.description}</p>
                  </div>
                  <Badge variant="purple">{result.rows} rows</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Bulk Downloads */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-accent-green" />
          Bulk Downloads
        </h2>
        <Card>
          <CardContent className="pt-6">
            <p className="text-gray-400 mb-4">
              BLAST search results stored in this database can be downloaded in tab-separated-values (TSV) format.
            </p>
            <div className="space-y-3">
              {bulkDownloads.map((download) => (
                <div key={download.filename} className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-white">{download.name}</h3>
                    <a
                      href={download.url}
                      className="flex items-center gap-2 px-3 py-1.5 bg-accent-green/20 text-accent-green rounded hover:bg-accent-green/30 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      {download.size}
                    </a>
                  </div>
                  <p className="text-xs text-gray-500 font-mono">{download.format}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Search and Browse Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-accent-green" />
          Search and Browse Options
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="hover:border-accent-cyan/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">By MIBiG Accession</h3>
              <p className="text-sm text-gray-400">
                Search for gene clusters using their MIBiG accession numbers (e.g., BGC0000001)
              </p>
            </CardContent>
          </Card>
          <Card className="hover:border-accent-purple/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">By Protein ID</h3>
              <p className="text-sm text-gray-400">
                Find genes by their NCBI protein identifiers or locus tags
              </p>
            </CardContent>
          </Card>
          <Card className="hover:border-accent-amber/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">By Compound Name</h3>
              <p className="text-sm text-gray-400">
                Search for reaction participants by compound name with wildcard support
              </p>
            </CardContent>
          </Card>
          <Card className="hover:border-accent-green/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">By Substructure</h3>
              <p className="text-sm text-gray-400">
                Search for compounds using SMARTS expressions for substructure matching
              </p>
            </CardContent>
          </Card>
          <Card className="hover:border-accent-cyan/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">BLAST Search</h3>
              <p className="text-sm text-gray-400">
                Search against MIBiG, PDB, SwissProt, or CDD databases with custom sequences
              </p>
            </CardContent>
          </Card>
          <Card className="hover:border-accent-purple/50 transition-colors">
            <CardContent className="pt-6">
              <h3 className="font-bold text-white mb-2">Browse Clusters</h3>
              <p className="text-sm text-gray-400">
                Browse all gene clusters with filters by biosynthetic class and organism
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.div>

      {/* Credits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center text-sm text-gray-500 pt-8 border-t border-white/10"
      >
        <p>
          Developed by{' '}
          <a
            href="http://www.bi.a.u-tokyo.ac.jp/~tterada/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-cyan hover:underline"
          >
            Tohru Terada
          </a>
          <sup>1</sup>, Ryota Kobayashi<sup>1</sup>, Koutaro Ukai<sup>1</sup>, Yoshitaka Moriwaki<sup>1,2</sup>, Chee Siang LEOW<sup>3</sup>, and Suguru Fujita<sup>1</sup>
        </p>
        <p className="mt-2 text-xs">
          <sup>1</sup>Computational Biology Laboratory, Department of Biotechnology, Graduate School of Agricultural and Life Sciences, The University of Tokyo, JAPAN
        </p>
        <p className="text-xs">
          <sup>2</sup>Computational Drug Discovery and Design Laboratory, Medical Research Laboratory, Institute of Science Tokyo, JAPAN
        </p>
        <p className="text-xs">
          <sup>3</sup>Graduate Faculty of Interdisciplinary Research Faculty of Engineering, Mechanical Engineering (Mechatronics), University of Yamanashi, JAPAN
        </p>
      </motion.div>
    </div>
  )
}
